import React from 'react';
import Button from '../button/Button';
import styles from './MiniTable.module.css';

interface Column<T> {
    key: string;
    header: string;
    width?: string;
    render?: (value: T) => React.ReactNode;
}

interface MiniTableProps<T> {
    columns: Column<T>[];
    data: T[];
    size?: 'small' | 'medium' | 'large';
    showPagination?: boolean;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    className?: string;
    headerTabs?: {
        label: string;
        active: boolean;
        onClick: () => void;
    }[];
    onRowClick?: (row: T) => void;
}

//미니 테이블 컴포넌트
function MiniTable<T>({
                          columns,
                          data,
                          size = 'medium',
                          showPagination = true,
                          page = 1,
                          totalPages = 1,
                          onPageChange,
                          className = '',
                          headerTabs,
                          onRowClick
                      }: MiniTableProps<T>) {

    // 데이터가 undefined이거나 배열이 아닌 경우 빈 배열로 처리
    const tableData = Array.isArray(data) ? data : [];

    return (
        <div className={`${styles.container} ${styles[size]} ${className}`}>
            {headerTabs && (
                <div className={styles.tabContainer}>
                    {/*탭버튼, 여러 테이블 사용 가능*/}
                    {headerTabs.map((tab, index) => (
                        <Button
                            key={index}
                            variant={tab.active ? "tab" : "tab-secondary"}
                            className={tab.active ? styles.activeTab : ''}
                            onClick={tab.onClick}
                        >
                            {tab.label}
                        </Button>
                    ))}
                </div>
            )}

            <div className={styles.tableWrapper}>
                {/*테이블*/}
                <table className={styles.table}>
                    <thead>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.key}
                                style={{width: column.width}}
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {tableData.map((item, rowIndex) => (
                        <tr
                            key={rowIndex}
                            onClick={() => onRowClick?.(item)}
                            className={`${styles.tableRow} ${onRowClick ? styles.clickableRow : ''}`}
                        >
                            {columns.map((column) => (
                                <td key={`${rowIndex}-${column.key}`}>
                                    {column.render
                                        ? column.render(item)
                                        : (item as any)[column.key]
                                    }
                                </td>
                            ))}
                        </tr>
                    ))}
                    {tableData.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} className={styles.noData}>
                                No data available
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/*페이지 버튼*/}
            {showPagination && (
                <div className={styles.pagination}>
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => onPageChange?.(page - 1)}
                        disabled={page <= 1}
                    >
                        &lt;
                    </Button>
                    {Array.from({length: totalPages}, (_, i) => i + 1).map((pageNum) => (
                        <Button
                            key={pageNum}
                            variant="secondary"
                            size="small"
                            className={pageNum === page ? styles.activePage : ''}
                            onClick={() => onPageChange?.(pageNum)}
                        >
                            {pageNum}
                        </Button>
                    ))}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => onPageChange?.(page + 1)}
                        disabled={page >= totalPages}
                    >
                        &gt;
                    </Button>
                </div>
            )}
        </div>
    );
}

export default MiniTable;